import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { colors, mediaQueries } from "./theme";
import { useApi } from "./Api/ApiContext";
import { useAsync, useAsyncFn } from "react-use";
import {
  Container,
  Button,
  ListGroup,
  ListGroupItem,
  Input,
  Navbar,
  NavbarToggler,
  NavbarBrand,
  Nav,
  NavItem,
  NavLink,
  Collapse,
} from "reactstrap";
import styled from "styled-components";
import useMeStore from "./store/MeStore";
import useCelebritiesStore from "./store/CelebritiesStore";
import ThemeToggle from "./ThemeToggle";
import { useCustomTheme } from "./Context/CustomThemeProvider";
import TiltHeaderImage, { HeaderImage } from "./component/HeaderImage";

const LogoBarWrapper = styled.div`
  margin: auto;
  padding: 5px 10px;
  background-color: ${({ theme }) => theme.primaryOne};
  ${mediaQueries.desktop} {
    padding: 10px 35px;
  }
`;
const HeaderContentWrapper = styled.div`
  margin: auto;
  padding: 5px 10px;
  background-color: ${({ theme }) => theme.body};
  }
  ${mediaQueries.desktop} {
    padding: 10px 35px;
  }
`;

const TopBar = styled.div`
  background-color: ${({ theme }) => theme.card};
  button,
  a {
    color: ${({ theme }) => theme.textPrimary} !important;
    &:hover {
      color: ${({ theme }) => theme.textPrimary};
      text-decoration: underline !important;
    }
  }
  padding: 15px 0;

  ${mediaQueries.desktop} {
    padding: 15px 38px;
  }
`;
const LogoTopBar = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  ${mediaQueries.desktop} {
    justify-content: space-between;
  }
`;

const SearchInput = styled.div`
  input::placeholder {
    color: ${({ theme }) => theme.text};
  }
  width: 450px;
  input {
    color: ${({ theme }) => theme.text};
    background: ${({ theme }) => theme.inputBgOnPrimary};
    :focus {
      color: ${({ theme }) => theme.text};
      background: ${({ theme }) => theme.inputBgOnPrimary};
    }
  }
`;

const SearchResult = styled.div`
  position: absolute;
  top: 50px;
  z-index: 1000;
  right: 0;
  width: 100%;
  padding: 2px;
  background-color: ${({ theme }) =>
    theme.mode === "light" ? "#5B5A5ACC" : theme.card};
  ${mediaQueries.desktop} {
    top: 60px;
    max-width: 500px;
  }
`;

const SearchResultImgWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: 20px;
  img {
    width: 100%;
    height: 100%;
  }
`;

const UserMenuWrapper = styled.div`
  position: relative;
`;

const UserMenu = styled(ListGroup)`
  width: 300px;
  background-color: ${({ theme }) =>
    theme.mode === "dark" ? theme.card : "#dadada"};
  position: absolute;
  top: 40px;
  right: 0px;
  z-index: 1001;
  li {
    background-color: red !important;
  }
`;

const Header = ({ celebrities }) => {
  const { getMe, logOut } = useApi();
  const [isOpen, setIsOpen] = useState(false);
  const toggleNavbar = () => setIsOpen(!isOpen);
  const { me, storeMe } = useMeStore();
  const { celebrities: celebritiesCatalogue } = useCelebritiesStore();
  const location = useLocation();
  const [searchResult, setSearchResult] = useState([]);
  const [showSearchResult, setShowSearchResult] = useState(false);
  const searchRef = useRef(null);
  const { theme, setTheme } = useCustomTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const { value: user } = useAsync(() => getMe().then((res) => storeMe(res)));

  // Les catégories sont déjà présentes dans la réponse de /celebrities
  // chargée une seule fois par App.js : pas de second appel ni de second
  // loader/message d'erreur dans le menu.
  const celebrityCategories = useMemo(() => {
    const byId = new Map();
    (celebritiesCatalogue?.items || []).forEach(({ category }) => {
      if (category?.slug && !byId.has(category.id ?? category.slug)) {
        byId.set(category.id ?? category.slug, category);
      }
    });
    return [...byId.values()].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "fr")
    );
  }, [celebritiesCatalogue]);

  useEffect(() => {
    setShowSearchResult(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResult(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isUserMenuOpen) {
      document.querySelector("body").classList.add("user-menu-open");
      return;
    }
    document.querySelector("body").classList.remove("user-menu-open");
  }, [isUserMenuOpen]);

  const toggleUserMenu = () => {
    setIsUserMenuOpen((prev) => !prev);
  };
  const isHome = location.pathname === "/";
  const [{ value }, logout] = useAsyncFn(async () => {
    return await logOut();
  }, []);

  const handleLogout = (e) => {
    e.preventDefault();
    logout().then((res) => {
      storeMe(undefined);
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isUserMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target)
      ) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const searchCelebrities = (e) => {
    const value = e.target.value.trim();

    if (!value) {
      setSearchResult([]);
      setShowSearchResult(false);
      return;
    }

    const results = celebrities?.filter(
      (c) =>
        c.givenName?.toLowerCase().startsWith(value.toLowerCase()) ||
        c.familyName?.toLowerCase().startsWith(value.toLowerCase()) ||
        c.nickname?.toLowerCase().startsWith(value.toLowerCase())
    );

    setSearchResult(results || []);
    setShowSearchResult(true);
  };

  return (
    <header className={"w-100"}>
      <TopBar>
        <Container className={"d-flex justify-content-end px-0"} fluid>
          <ThemeToggle />
          {me ? (
            <UserMenuWrapper ref={userMenuRef}>
              <Button color="link" onClick={toggleUserMenu}>
                {me.nickname ? me.nickname : me.givenName + " " + me.familyName}
              </Button>
              <Collapse isOpen={isUserMenuOpen}>
                <UserMenu>
                  <ListGroupItem
                    action
                    tag={RouterLink}
                    to={"/admin/profile"}
                    active={location.pathname === "/admin/profile"}
                  >
                    Mon profil
                  </ListGroupItem>
                  <ListGroupItem
                    action
                    tag={RouterLink}
                    to={"/admin"}
                    active={location.pathname === "/admin"}
                  >
                    Tableau de bord
                  </ListGroupItem>
                  <ListGroupItem
                    action
                    tag={RouterLink}
                    to={"/admin/orders"}
                    active={location.pathname === "/admin/orders"}
                  >
                    Mes commandes
                  </ListGroupItem>
                  <ListGroupItem
                    action
                    tag={RouterLink}
                    to={"/admin/payments"}
                    active={location.pathname === "/admin/payments"}
                  >
                    Historique des paiements
                  </ListGroupItem>
                  <ListGroupItem
                    action
                    tag={RouterLink}
                    to={"/admin/products"}
                    active={location.pathname === "/admin/products"}
                  >
                    Mes produits
                  </ListGroupItem>
                  <ListGroupItem tag={RouterLink} onClick={handleLogout}>
                    Déconnexion
                  </ListGroupItem>
                </UserMenu>
              </Collapse>
            </UserMenuWrapper>
          ) : (
            <div>
              <RouterLink to="/auth/login">
                <Button
                  color={"link"}
                  className={"text-decoration-none fw-bold"}
                >
                  Connexion
                </Button>
              </RouterLink>
              |
              <RouterLink to="/auth/sign-up">
                <Button
                  color={"link"}
                  className={"text-decoration-none fw-bold"}
                >
                  Inscription
                </Button>
              </RouterLink>
            </div>
          )}
        </Container>
      </TopBar>
      <LogoBarWrapper>
        <Container fluid className={"px-0"}>
          <LogoTopBar>
            <RouterLink to={"/"}>
              <Button
                color={"link"}
                className={"text-decoration-none text-white fw-bold"}
              >
                <img width={100} src="/logo.png" alt="logo du site" />
              </Button>
            </RouterLink>
            <SearchInput ref={searchRef}>
              <Input
                type="search"
                placeholder={"Rechercher une célébrité"}
                className={"border-0 w-100 mx-auto"}
                onChange={searchCelebrities}
                onFocus={() => {
                  if (searchResult.length > 0) {
                    setShowSearchResult(true);
                  }
                }}
              />
              {showSearchResult && searchResult?.length > 0 && (
                <SearchResult>
                  <ListGroup>
                    {searchResult.map((c, index) => (
                      <NavLink
                        key={index}
                        tag={RouterLink}
                        to={`/shop/${c.id}/infos`}
                      >
                        <ListGroupItem
                          className={
                            "d-flex border-start-0 border-end-0 border-top-0"
                          }
                        >
                          <SearchResultImgWrapper>
                            <img
                              src={c.avatar}
                              title={`photo de ${c.givenName} ${c.familyName}`}
                              alt={`photo de ${c.givenName} ${c.familyName}`}
                              className={"object-fit-cover"}
                            />
                          </SearchResultImgWrapper>
                          <div>
                            <strong>
                              {`${c.givenName} ${c.familyName} `}{" "}
                            </strong>
                            <div>
                              {c.nickname ? `(alias ${c.nickname})` : ""}
                            </div>
                          </div>
                        </ListGroupItem>
                      </NavLink>
                    ))}
                  </ListGroup>
                </SearchResult>
              )}
            </SearchInput>
          </LogoTopBar>
        </Container>
      </LogoBarWrapper>

      <HeaderContentWrapper>
        <Container fluid className={"px-0"}>
          <Navbar
            color={"faded"}
            dark={theme === "dark"}
            expand={"lg"}
            sticky={"position"}
          >
            <NavbarBrand tag={RouterLink} to="/">
              Accueil
            </NavbarBrand>
            <NavbarToggler onClick={toggleNavbar} />
            <Collapse isOpen={isOpen} navbar>
              <Nav className="me-auto" navbar>
                {celebrityCategories.map((category) => (
                  <NavItem
                    onClick={toggleNavbar}
                    key={category.id ?? category.slug}
                  >
                    <NavLink
                      className={"fs-6"}
                      tag={RouterLink}
                      to={`/shop/categories/${category.slug}`}
                      active={
                        location.pathname ===
                        `/shop/categories/${category.slug}`
                      }
                    >
                      {category.name}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            </Collapse>
          </Navbar>
        </Container>
      </HeaderContentWrapper>
      {isHome && (
        <HeaderContentWrapper>
          <Container fluid>
            <HeaderImage />
          </Container>
        </HeaderContentWrapper>
      )}
    </header>
  );
};

export default Header;
